import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  PrismCard,
  PrismMetricBlock,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { notEq } from 'ember-truth-helpers';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelMarketInsightChartsChartOverallSearches from 'lumen/components/hotel/market-insight/charts/chart-overall-searches';
import HotelMarketInsightChartsChartSearchPatternsLosGeneric from 'lumen/components/hotel/market-insight/charts/chart-search-patterns-los-generic';
import HotelMarketInsightListsListCountriesSearching from 'lumen/components/hotel/market-insight/lists/list-countries-searching';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import {
  MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
  MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
  MI_SEARCHES_TYPE_HOTELS_BUSINESS,
  MI_SEARCHES_TYPE_HOTELS_LEISURE,
} from 'lumen/utils/constants/general-constants';
import { MI_SEARCHES_TYPE_TO_SEARCHES_TAB_MAPPING } from 'lumen/utils/constants/market-insight-constants';

interface HotelMarketInsightCardsSearchesDetailsSignature {
  Args: {
    currentExtractDate: unknown;
    isCalendarLoading: unknown;
    isDetailLoading: unknown;
    searchesTooltipText: unknown;
    selectedDayModel: unknown;
    updateSearchesDetailsItem: unknown;
  };
}

export default class HotelMarketInsightCardsSearchesDetails extends Component<HotelMarketInsightCardsSearchesDetailsSignature> {
  @service declare intl: Services['intl'];
  @service declare miSearches: Services['mi-searches'];
  @service declare miSelections: Services['mi-selections'];

  /**
   * returns country searches used in the country search list (for flights)
   * This needs to be passed to the component so we can use the same component with the aggregated searches for the overview
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get countrySearchesSearchTypeForCurrentDayFilteredByExtractDate() {
    let result = [];

    if (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.selectedDayModel.countrysearches.length &&
      this.args.currentExtractDate
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      result = this.miSearches.filterCountrySearchesByExtractDate(
        // @ts-expect-error: Incorrect type
        this.selectedSearchType.id,
        this.args.selectedDayModel,
        this.args.currentExtractDate,
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasFlightBusinessSearches() {
    return this.miSearches
      ?.areFlightBusinessSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasFlightLeisureSearches() {
    return this.miSearches
      ?.areFlightLeisureSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelBusinessSearches() {
    return this.miSearches
      ?.areHotelBusinessSearchesSupportedForTheCurrentDestination;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasHotelLeisureSearches() {
    return this.miSearches
      ?.areHotelLeisureSearchesSupportedForTheCurrentDestination;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchOptions() {
    const searchOptions = [];
    const metaOta = `${this.intl.t('general.meta')}/${this.intl.t('general.ota')}`;
    const gds = `${this.intl.t('general.GDS')}`;

    if (this.hasFlightLeisureSearches) {
      const id = MI_SEARCHES_TYPE_FLIGHTS_LEISURE;
      const label = this.intl.t('market_insight.searches.flights');

      if (this.hasFlightBusinessSearches) {
        searchOptions.push({
          id,
          label: `${label} (${metaOta})`,
          icon: 'plane',
        });
      } else if (this.hasHotelBusinessSearches) {
        searchOptions.push({
          id,
          label: `${label} (${metaOta}/${gds})`,
          icon: 'plane',
        });
      } else {
        searchOptions.push({
          id,
          label,
          icon: 'plane',
        });
      }
    }

    if (this.hasFlightBusinessSearches) {
      searchOptions.push({
        id: MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
        label: `${this.intl.t('market_insight.searches.flights')} (${gds})`,
        icon: 'plane',
      });
    }

    if (this.hasHotelLeisureSearches) {
      const id = MI_SEARCHES_TYPE_HOTELS_LEISURE;
      const label = this.intl.t('market_insight.searches.hotels');

      if (this.hasHotelBusinessSearches) {
        searchOptions.push({
          id,
          label: `${label} (${metaOta})`,
          icon: 'business-type-hotel',
        });
      } else if (this.hasFlightBusinessSearches) {
        searchOptions.push({
          id,
          label: `${label} (${metaOta}/${gds})`,
          icon: 'business-type-hotel',
        });
      } else {
        searchOptions.push({
          id,
          label,
          icon: 'business-type-hotel',
        });
      }
    }

    if (this.hasHotelBusinessSearches) {
      searchOptions.push({
        id: MI_SEARCHES_TYPE_HOTELS_BUSINESS,
        label: `${this.intl.t('market_insight.searches.hotels')} (${gds})`,
        icon: 'business-type-hotel',
      });
    }

    return searchOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedSearchType() {
    const {
      isFlightBusinessSearchesDetailsItemActive,
      isFlightSearchesDetailsItemActive,
      isHotelBusinessSearchesDetailsItemActive,
      isHotelSearchesDetailsItemActive,
    } = this.miSelections;

    if (isFlightSearchesDetailsItemActive) {
      return (
        // because isFlightSearchesDetailsItemActive is a qp default param is possible the option doesn't exist, that is why we need to add the option to return this.searchOptions[0]
        this.searchOptions.find(
          (option) => option.id === MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
        ) ?? this.searchOptions[0]
      );
    }

    if (isFlightBusinessSearchesDetailsItemActive) {
      return this.searchOptions.find(
        (option) => option.id === MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
      );
    }

    if (isHotelSearchesDetailsItemActive) {
      return this.searchOptions.find(
        (option) => option.id === MI_SEARCHES_TYPE_HOTELS_LEISURE,
      );
    }

    if (isHotelBusinessSearchesDetailsItemActive) {
      return this.searchOptions.find(
        (option) => option.id === MI_SEARCHES_TYPE_HOTELS_BUSINESS,
      );
    }

    return this.searchOptions[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedSearchTypeSummary() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.miSearches.getSearchSummaryForCurrentSelection(
      // @ts-expect-error: Incorrect type
      this.selectedSearchType.id,
      this.args.isDetailLoading,
      this.args.isCalendarLoading,
      this.args.selectedDayModel,
      this.args.currentExtractDate,
    );
  }

  @action
  // @ts-expect-error: Incorrect type
  searchTypeChanged(searchType): void {
    // @ts-expect-error: Incorrect type
    this.args.updateSearchesDetailsItem(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      MI_SEARCHES_TYPE_TO_SEARCHES_TAB_MAPPING[searchType.id],
    );
  }

  <template>
    {{! @glint-nocheck }}
    <PrismCard
      class="detail-pane-card detail-pane-card--searches"
      data-test-id="market-insight-searches-details"
    >
      <:header as |Header|>
        <div class="flex items-center gap-100">
          <Header.title>
            {{t "market_insight.searches_in_the_last_days" count=7}}
          </Header.title>
          <GeneralIconWithTooltip
            @side="right"
            @tooltipContent={{@searchesTooltipText}}
          />
        </div>
      </:header>
      <:content>
        <div
          class="searches-dropdown flex justify-end"
          data-test-id="searches-details-dropdown"
        >
          <PrismSelectInput
            @horizontalPosition="right"
            @isSmall={{true}}
            @onChange={{this.searchTypeChanged}}
            @options={{this.searchOptions}}
            @value={{this.selectedSearchType}}
          />
        </div>

        {{! Search level card }}
        <PrismCard @isSecondary={{true}}>
          <:header>
            <p class="text-300--bold">{{t
                "market_insight.search_patterns.search_level"
              }}</p>
          </:header>
          <:content>
            <PrismMetricBlock @align="left">
              <:value>
                <span
                  class="text-400--bold"
                >{{this.selectedSearchTypeSummary.numSearchesAsLabel}}</span>
              </:value>
              <:label>{{t
                  "market_insight.searches.search_level.vs_usual"
                  dow=(dayjsFormat @selectedDayModel.date format="dddd")
                }}</:label>
            </PrismMetricBlock>

            <HotelMarketInsightChartsChartOverallSearches
              @currentExtractDate={{@currentExtractDate}}
              @dayModel={{@selectedDayModel}}
              @searchesType={{this.selectedSearchType.id}}
            />
          </:content>
        </PrismCard>

        {{! Most searched length-of-stay (LOS) }}
        <PrismCard @isSecondary={{true}} class="mt-400">
          <:header>
            <p class="text-300--bold">{{t
                "market_insight.searches.los_title"
              }}</p>
          </:header>
          <:content>
            {{#unless @isDetailLoading}}
              <HotelMarketInsightChartsChartSearchPatternsLosGeneric
                @currentExtractDate={{@currentExtractDate}}
                @dayModel={{@selectedDayModel}}
                @isDetailLoading={{this.isDetailLoading}}
                @searchesType={{this.selectedSearchType.id}}
              />
            {{/unless}}
          </:content>
        </PrismCard>

        {{! Top searching countries }}
        {{#if
          (notEq this.selectedSearchType.id MI_SEARCHES_TYPE_HOTELS_BUSINESS)
        }}
          {{#if
            this.countrySearchesSearchTypeForCurrentDayFilteredByExtractDate.length
          }}
            <PrismCard
              @contentLayout="noPadding"
              @isSecondary={{true}}
              class="mt-400"
              data-test-id="searches-details-top-countries"
            >
              <:header>
                <p class="text-300--bold">{{t
                    "market_insight.top_searching_countries"
                  }}</p>
              </:header>
              <:content>
                <HotelMarketInsightListsListCountriesSearching
                  @activeSearchesType={{this.selectedSearchType.trackingValue}}
                  @countrySearches={{this.countrySearchesSearchTypeForCurrentDayFilteredByExtractDate}}
                  @wheelPropagation={{true}}
                />
              </:content>
            </PrismCard>
          {{/if}}
        {{/if}}
      </:content>
    </PrismCard>
  </template>
}
